import { Suspense, lazy } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { SessionTimeoutModal } from './pages/auth/SessionTimeoutModal';

// MN1: SocialCardBuilder + CardEditor are not in the v1 product spec.
// Feature-flagged off in production until spec alignment is confirmed.
// Set VITE_ENABLE_CARD_BUILDER=true to enable in staging/dev.
const CARD_BUILDER_ENABLED = import.meta.env.VITE_ENABLE_CARD_BUILDER === 'true' ||
  import.meta.env.MODE === 'development';
import { ProtectedRoute, RoleGuard, WorkspaceManagerGuard } from './components/guards/ProtectedRoute';
import { ErrorBoundary, PageLoader } from '@jn/ui/components/feedback/ErrorBoundary';
import { HomeRedirect } from './components/HomeRedirect';
import { PublicTopBar } from './components/layout/PublicTopBar';
import { ROUTES } from './routes';

// ── Lazy-loaded pages ──────────────────────────────────────────────────────────
const LoginPage           = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const OwnerInvitePage     = lazy(() => import('./pages/auth/OwnerInvitePage').then(m => ({ default: m.OwnerInvitePage })));
const ActivationPage      = lazy(() => import('./pages/auth/ActivationPage').then(m => ({ default: m.ActivationPage })));
const EditorialLayout     = lazy(() => import('./pages/editorial/EditorialLayout').then(m => ({ default: m.EditorialLayout })));
const FeedPage            = lazy(() => import('./pages/editorial/FeedPage').then(m => ({ default: m.FeedPage })));
const StoryDetailPage     = lazy(() => import('./pages/editorial/StoryDetailPage').then(m => ({ default: m.StoryDetailPage })));
const PublishQueuePage    = lazy(() => import('./pages/publishing/PublishQueuePage').then(m => ({ default: m.PublishQueuePage })));
const PostedContentPage   = lazy(() => import('./pages/calendar/PostedContentPage').then(m => ({ default: m.PostedContentPage })));
const ContentCalendarPage = lazy(() => import('./pages/calendar/ContentCalendarPage').then(m => ({ default: m.ContentCalendarPage })));
const AnalyticsDashboard  = lazy(() => import('./pages/analytics/AnalyticsDashboardPage').then(m => ({ default: m.AnalyticsDashboardPage })));
const AdminLayout         = lazy(() => import('./pages/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const SuperAdminLoginPage = lazy(() => import('./pages/admin/SuperAdminLoginPage').then(m => ({ default: m.SuperAdminLoginPage })));
const PlansPage           = lazy(() => import('./pages/admin/PlansPage').then(m => ({ default: m.PlansPage })));
const AdminSettingsPage   = lazy(() => import('./pages/admin/AdminSettingsPage').then(m => ({ default: m.AdminSettingsPage })));
const TenantListPage      = lazy(() => import('./pages/admin/TenantListPage').then(m => ({ default: m.TenantListPage })));
const TenantDetailPage    = lazy(() => import('./pages/admin/TenantDetailPage').then(m => ({ default: m.TenantDetailPage })));
const TenantWizard        = lazy(() => import('./pages/admin/TenantOnboardingWizard').then(m => ({ default: m.TenantOnboardingWizard })));
const TenantEditPage      = lazy(() => import('./pages/admin/TenantEditPage').then(m => ({ default: m.TenantEditPage })));
const AdminHomeRedirect   = lazy(() => import('./pages/admin/AdminHomeRedirect').then(m => ({ default: m.AdminHomeRedirect })));
const UserListPage        = lazy(() => import('./pages/users/UserListPage').then(m => ({ default: m.UserListPage })));
const EditorialTeamPage   = lazy(() => import('./pages/users/UserListPage').then(m => ({ default: m.EditorialTeamPage })));
const RssFeedsPage        = lazy(() => import('./pages/editorial/RssFeedsPage').then(m => ({ default: m.RssFeedsPage })));
const AddSourceWizard     = lazy(() => import('./pages/editorial/AddSourceWizard').then(m => ({ default: m.AddSourceWizard })));
const AddSourceFieldsPage = lazy(() => import('./pages/editorial/AddSourceFieldsPage').then(m => ({ default: m.AddSourceFieldsPage })));
const AddSourceCheckPage  = lazy(() => import('./pages/editorial/AddSourceCheckPage').then(m => ({ default: m.AddSourceCheckPage })));
const SourceDetailPage    = lazy(() => import('./pages/editorial/SourceDetailPage').then(m => ({ default: m.SourceDetailPage })));
const SourceCoveragePage  = lazy(() => import('./pages/editorial/SourceCoveragePage').then(m => ({ default: m.SourceCoveragePage })));
const TrendSignalsExplorerPage = lazy(() =>
  import('./pages/editorial/TrendSignalsExplorerPage').then(m => ({ default: m.default }))
);
const WebTrendsPage = lazy(() =>
  import('./pages/editorial/WebTrendsPage').then(m => ({ default: m.default }))
);
const HeadlinesIntegrationSettingsPage = lazy(() =>
  import('./pages/editorial/HeadlinesIntegrationSettingsPage').then(m => ({ default: m.HeadlinesIntegrationSettingsPage })),
);
const SocialCardBuilderPage = lazy(() =>
  import('./pages/editorial/SocialCardBuilderPage').then(m => ({ default: m.SocialCardBuilderPage })),
);
const ManualCreatePage = lazy(() =>
  import('./pages/editorial/ManualCreatePage').then(m => ({ default: m.ManualCreatePage })),
);
const HeadlineSelectionPage = lazy(() =>
  import('./pages/editorial/HeadlineSelectionPage').then(m => ({ default: m.HeadlineSelectionPage })),
);
const CardEditorPage = lazy(() =>
  import('./pages/CardEditorPage').then(m => ({ default: m.CardEditorPage })),
);
const CardStudioPage = lazy(() =>
  import('./pages/CardStudioPage').then(m => ({ default: m.CardStudioPage })),
);
const TemplateLibraryPage = lazy(() =>
  import('./pages/templateStudio/TemplateLibraryPage').then(m => ({ default: m.TemplateLibraryPage })),
);
const TemplateCreatePage = lazy(() =>
  import('./pages/templateStudio/TemplateCreatePage').then(m => ({ default: m.TemplateCreatePage })),
);
const TemplateDuplicatePage = lazy(() =>
  import('./pages/templateStudio/TemplateDuplicatePage').then(m => ({ default: m.TemplateDuplicatePage })),
);
const TemplateFigmaPage = lazy(() =>
  import('./pages/templateStudio/TemplateFigmaPage').then(m => ({ default: m.TemplateFigmaPage })),
);
const TemplateFigmaConnectedPage = lazy(() =>
  import('./pages/templateStudio/TemplateFigmaConnectedPage').then(m => ({ default: m.TemplateFigmaConnectedPage })),
);
const TemplateUploadPage = lazy(() =>
  import('./pages/templateStudio/TemplateUploadPage').then(m => ({ default: m.TemplateUploadPage })),
);
const TemplateCanvasPage = lazy(() =>
  import('./pages/templateStudio/TemplateCanvasPage').then(m => ({ default: m.TemplateCanvasPage })),
);
const TemplateEditorPage = lazy(() =>
  import('./pages/templateStudio/TemplateEditorPage').then(m => ({ default: m.TemplateEditorPage })),
);
const TemplateMappingPage = lazy(() =>
  import('./pages/templateStudio/TemplateMappingPage').then(m => ({ default: m.TemplateMappingPage })),
);
const TemplatePreviewPage = lazy(() =>
  import('./pages/templateStudio/TemplatePreviewPage').then(m => ({ default: m.TemplatePreviewPage })),
);
const TemplateItemRedirect = lazy(() =>
  import('./pages/templateStudio/TemplateItemRedirect').then(m => ({ default: m.TemplateItemRedirect })),
);
const AuditLogPage        = lazy(() => import('./pages/users/AuditLogPage').then(m => ({ default: m.AuditLogPage })));
const PasswordResetRequestPage = lazy(() =>
  import('./pages/auth/PasswordResetPage').then(m => ({ default: m.PasswordResetRequestPage }))
);
const SetPasswordPage = lazy(() =>
  import('./pages/auth/SetPasswordPage').then(m => ({ default: m.SetPasswordPage }))
);
const LegacyResetConfirmRedirect = lazy(() =>
  import('./pages/auth/SetPasswordPage').then(m => ({ default: m.LegacyResetConfirmRedirect }))
);
const IntegrationsPage = lazy(() =>
  import('./pages/settings/IntegrationsPage').then(m => ({ default: m.IntegrationsPage }))
);

/**
 * SP-022 — Story detail overlay (`StoryDetailPanel` parity): when `location.state.background`
 * is set, the primary route tree renders that background (feed, etc.) while a second tree
 * renders `StoryDetailPage` above it. Direct / deep links omit `background` → full-page detail.
 */
function AppRoutes() {
  const location = useLocation();
  const background = (location.state as { background?: Location } | undefined)?.background;

  return (
    <>
      <Routes location={background ?? location}>
        {/* Public */}
        <Route path={ROUTES.ADMIN_LOGIN}  element={<SuperAdminLoginPage />} />
        <Route path={ROUTES.LOGIN}        element={<LoginPage />} />
        <Route path={ROUTES.OWNER_INVITE} element={<OwnerInvitePage />} />
        <Route path={ROUTES.ACTIVATE}     element={<ActivationPage />} />
        <Route path="/auth/password-reset"         element={<PasswordResetRequestPage />} />
        <Route path="/auth/set-password"           element={<SetPasswordPage />} />
        {/* Links issued before the set-password route existed. */}
        <Route path="/auth/password-reset/confirm" element={<LegacyResetConfirmRedirect />} />
        <Route
          path={ROUTES.FORBIDDEN}
          element={
            <div className="flex min-h-screen flex-col bg-neutral-50">
              <PublicTopBar />
              <div className="flex flex-1 items-center justify-center text-neutral-500">403 — Access denied</div>
            </div>
          }
        />
        <Route
          path={ROUTES.NOT_FOUND}
          element={
            <div className="flex min-h-screen flex-col bg-neutral-50">
              <PublicTopBar />
              <div className="flex flex-1 items-center justify-center text-neutral-500">404 — Page not found</div>
            </div>
          }
        />

        {/* Editorial (authenticated) */}
        <Route element={<ProtectedRoute><EditorialLayout /></ProtectedRoute>}>
          <Route path={ROUTES.EDITORIAL}              element={<FeedPage />} />
          <Route path={ROUTES.EDITORIAL_STORY()}      element={<StoryDetailPage />} />
          <Route path={ROUTES.SEARCH}                 element={<FeedPage />} />
          <Route path={ROUTES.PUBLISH_QUEUE}          element={<PublishQueuePage />} />
          <Route path={ROUTES.CONTENT_CALENDAR}        element={<ContentCalendarPage />} />
          <Route path={ROUTES.POSTED_CONTENT}         element={<PostedContentPage />} />
          <Route path={ROUTES.ANALYTICS}              element={<AnalyticsDashboard />} />
          <Route
            path={ROUTES.EDITORIAL_TEAM}
            element={
              <RoleGuard permission="users:read">
                <EditorialTeamPage />
              </RoleGuard>
            }
          />
          <Route
            path={ROUTES.EDITORIAL_RSS_FEEDS}
            element={
              <RoleGuard permission="users:write">
                <RssFeedsPage />
              </RoleGuard>
            }
          />
          <Route
            path={ROUTES.EDITORIAL_FEED_SOURCES_ADD}
            element={
              <RoleGuard permission="users:write">
                <AddSourceWizard />
              </RoleGuard>
            }
          />
          <Route
            path={ROUTES.EDITORIAL_FEED_SOURCES_FIELDS()}
            element={
              <RoleGuard permission="users:write">
                <AddSourceFieldsPage />
              </RoleGuard>
            }
          />
          <Route
            path={ROUTES.EDITORIAL_FEED_SOURCES_CHECK()}
            element={
              <RoleGuard permission="users:write">
                <AddSourceCheckPage />
              </RoleGuard>
            }
          />
          <Route
            path={ROUTES.EDITORIAL_FEED_SOURCES_COVERAGE}
            element={
              <RoleGuard permission="users:write">
                <SourceCoveragePage />
              </RoleGuard>
            }
          />
          <Route
            path={ROUTES.EDITORIAL_FEED_SOURCES_DETAIL()}
            element={
              <RoleGuard permission="users:write">
                <SourceDetailPage />
              </RoleGuard>
            }
          />
          <Route
            path={ROUTES.EDITORIAL_TREND_SIGNALS}
            element={
              <RoleGuard permission="users:write">
                <TrendSignalsExplorerPage />
              </RoleGuard>
            }
          />
          {/* Template Studio — workspace Owner/Admin only; others are redirected silently */}
          <Route path={ROUTES.TEMPLATE_STUDIO}          element={<WorkspaceManagerGuard><TemplateLibraryPage /></WorkspaceManagerGuard>} />
          <Route path={ROUTES.TEMPLATE_STUDIO_NEW}      element={<WorkspaceManagerGuard><TemplateCreatePage /></WorkspaceManagerGuard>} />
          <Route path={ROUTES.TEMPLATE_STUDIO_DUPLICATE} element={<WorkspaceManagerGuard><TemplateDuplicatePage /></WorkspaceManagerGuard>} />
          <Route path={ROUTES.TEMPLATE_STUDIO_UPLOAD}   element={<WorkspaceManagerGuard><TemplateUploadPage /></WorkspaceManagerGuard>} />
          <Route path={ROUTES.TEMPLATE_STUDIO_FIGMA}    element={<WorkspaceManagerGuard><TemplateFigmaPage /></WorkspaceManagerGuard>} />
          <Route path={ROUTES.TEMPLATE_STUDIO_CANVAS}   element={<WorkspaceManagerGuard><TemplateCanvasPage /></WorkspaceManagerGuard>} />
          <Route path={ROUTES.TEMPLATE_STUDIO_ITEM()}   element={<WorkspaceManagerGuard><TemplateItemRedirect /></WorkspaceManagerGuard>} />
          <Route path={ROUTES.EDITORIAL_WEB_TRENDS}          element={<WebTrendsPage />} />
          <Route path={ROUTES.EDITORIAL_HEADLINES_INTEGRATION} element={<HeadlinesIntegrationSettingsPage />} />
          <Route path={ROUTES.SETTINGS_INTEGRATIONS}          element={<IntegrationsPage />} />
          <Route path={ROUTES.EDITORIAL_CREATE}          element={<ManualCreatePage />} />
          {/* MN1: Card builder not in v1 spec — gated by VITE_ENABLE_CARD_BUILDER */}
          {CARD_BUILDER_ENABLED && (
            <Route path={ROUTES.EDITORIAL_SOCIAL_CARDS} element={<SocialCardBuilderPage />} />
          )}
          <Route path={ROUTES.EDITORIAL_HEADLINES()}     element={<HeadlineSelectionPage />} />
        </Route>

        {/* MN1: Card editor — full-screen outside EditorialLayout; gated by VITE_ENABLE_CARD_BUILDER */}
        {CARD_BUILDER_ENABLED && (
          <Route
            path={ROUTES.CARD_EDITOR}
            element={<ProtectedRoute><CardEditorPage /></ProtectedRoute>}
          />
        )}

        {/* Figma OAuth popup landing — bare page that reports back to the opener and closes */}
        <Route
          path={ROUTES.TEMPLATE_STUDIO_FIGMA_CONNECTED}
          element={<ProtectedRoute><TemplateFigmaConnectedPage /></ProtectedRoute>}
        />
        {/* Template Studio editor — full screen, outside EditorialLayout (sidebar hidden), Owner/Admin only */}
        <Route
          path={ROUTES.TEMPLATE_STUDIO_NEW_EDITOR}
          element={<ProtectedRoute><WorkspaceManagerGuard><TemplateEditorPage /></WorkspaceManagerGuard></ProtectedRoute>}
        />
        <Route
          path={ROUTES.TEMPLATE_STUDIO_PREVIEW()}
          element={<ProtectedRoute><WorkspaceManagerGuard><TemplatePreviewPage /></WorkspaceManagerGuard></ProtectedRoute>}
        />
        <Route
          path={ROUTES.TEMPLATE_STUDIO_MAP()}
          element={<ProtectedRoute><WorkspaceManagerGuard><TemplateMappingPage /></WorkspaceManagerGuard></ProtectedRoute>}
        />
        <Route
          path={ROUTES.TEMPLATE_STUDIO_EDIT()}
          element={<ProtectedRoute><WorkspaceManagerGuard><TemplateEditorPage /></WorkspaceManagerGuard></ProtectedRoute>}
        />

        {/* Unified Card Studio — primary entry point for all card creation */}
        {CARD_BUILDER_ENABLED && (
          <Route
            path={ROUTES.CARD_STUDIO}
            element={<ProtectedRoute><CardStudioPage /></ProtectedRoute>}
          />
        )}

        {/* Admin (Admin + SuperAdmin) — platform dashboard */}
        <Route element={<ProtectedRoute><RoleGuard permission="admin:tenants"><AdminLayout /></RoleGuard></ProtectedRoute>}>
          <Route path={ROUTES.ADMIN}                  element={<AdminHomeRedirect />} />
          <Route path={ROUTES.ADMIN_PLANS}            element={<PlansPage />} />
          <Route path={ROUTES.ADMIN_SETTINGS}         element={<AdminSettingsPage />} />
          <Route path={ROUTES.ADMIN_TENANTS}          element={<TenantListPage />} />
          <Route path={ROUTES.ADMIN_TENANTS_NEW}      element={<Navigate to={ROUTES.ADMIN_TENANTS_NEW_STEP('details')} replace />} />
          <Route path={ROUTES.ADMIN_TENANTS_NEW_STEP()} element={<TenantWizard />} />
          <Route path="/admin/tenants/:id/edit"       element={<TenantEditPage />} />
          <Route path="/admin/tenants/:id"            element={<TenantDetailPage />} />
          <Route path={ROUTES.ADMIN_USERS}            element={<UserListPage />} />
          <Route path="/admin/users/:id"              element={<UserListPage />} />
          <Route path={ROUTES.ADMIN_AUDIT}            element={<AuditLogPage />} />
        </Route>

        {/* Root — SuperAdmin/Admin → admin area; others → editorial */}
        <Route path="/" element={<HomeRedirect />} />
        <Route path="*"  element={<Navigate to={ROUTES.NOT_FOUND} replace />} />
      </Routes>
      {background ? (
        <Routes>
          <Route element={<ProtectedRoute><Outlet /></ProtectedRoute>}>
            <Route path={ROUTES.EDITORIAL_STORY()} element={<StoryDetailPage />} />
          </Route>
        </Routes>
      ) : null}
    </>
  );
}

export default function App() {
  const { timeoutWarning, extendSession, logout } = useAuth();

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <AppRoutes />
        <SessionTimeoutModal
          open={timeoutWarning.show}
          secondsRemaining={timeoutWarning.secondsRemaining}
          onExtend={extendSession}
          onLogout={logout}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
